/**
 * Centralized Backend URL Configuration
 *
 * This utility provides consistent access to backend URLs throughout the application.
 * All hardcoded localhost:4000 references should use this instead.
 *
 * USAGE:
 * - In Vue components/pages: use the composable useBackendUrl()
 * - In server routes: use getBackendUrl(event)
 * - In non-Nuxt contexts: use DEFAULT_BACKEND_URL as fallback
 */

// Default fallback URL for development
export const DEFAULT_BACKEND_URL = "http://localhost:4000";
export const DEFAULT_API_BASE = "http://localhost:4000/api";

/**
 * Get the backend base URL from runtime config
 * Use this in Vue components, pages, and composables
 */
export function useBackendUrl() {
  const config = useRuntimeConfig();

  // Remove /api suffix if present to get base URL
  const apiBase = (config.public.apiBase as string) || DEFAULT_API_BASE;
  const baseUrl = apiBase.replace(/\/api\/?$/, "") || DEFAULT_BACKEND_URL;

  return {
    /** Base URL without /api (e.g., http://localhost:4000) */
    baseUrl,
    /** API base URL with /api (e.g., http://localhost:4000/api) */
    apiBase: apiBase.endsWith("/api") ? apiBase : `${apiBase}/api`,
    /** Get full URL for a path */
    getUrl: (path: string) => {
      const cleanPath = path.startsWith("/") ? path : `/${path}`;
      return `${baseUrl}${cleanPath}`;
    },
    /** Get full API URL for an endpoint */
    getApiUrl: (endpoint: string) => {
      const cleanEndpoint = endpoint.startsWith("/")
        ? endpoint.slice(1)
        : endpoint;
      const base = apiBase.endsWith("/api") ? apiBase : `${apiBase}/api`;
      return `${base}/${cleanEndpoint}`;
    },
  };
}

/**
 * Get backend URL in server routes
 * Use this in server/api handlers
 */
export function getServerBackendUrl(): {
  baseUrl: string;
  apiBase: string;
} {
  const config = useRuntimeConfig();
  const apiBase = (config.public.apiBase as string) || DEFAULT_API_BASE;
  const baseUrl = apiBase.replace(/\/api\/?$/, "") || DEFAULT_BACKEND_URL;

  return {
    baseUrl,
    apiBase: apiBase.endsWith("/api") ? apiBase : `${apiBase}/api`,
  };
}
